import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  FileText, Star, UploadCloud, RefreshCw, Mail, Activity, Loader2, Check,
} from "lucide-react";

const ICON_BY_KIND = {
  "file-text": FileText,
  "star": Star,
  "upload": UploadCloud,
  "refresh": RefreshCw,
  "mail": Mail,
};

/**
 * Per-client (per browser) "last seen" timestamp for the activity feed.
 * Stored in localStorage so the unread badge survives reloads but is
/**
 * Per-tab "last seen activity" timestamp. NOT sensitive data — just a
 * UI-noise marker so already-seen feed items render dimmed. Using
 * sessionStorage (cleared on tab close) instead of localStorage so the
 * footprint is minimal and XSS-leak surface is zero.
 */
const LAST_SEEN_KEY = (clientScope) => `activity_last_seen:${clientScope || "self"}`;

function readLastSeen(scope) {
  try {
    const raw = sessionStorage.getItem(LAST_SEEN_KEY(scope));
    return raw ? new Date(raw).getTime() : 0;
  } catch { return 0; }
}

function writeLastSeen(scope, ts) {
  try { sessionStorage.setItem(LAST_SEEN_KEY(scope), new Date(ts).toISOString()); }
  catch { /* private-mode etc — silently ignore */ }
}

export default function ActivityFeed({ accent }) {
  const [params] = useSearchParams();
  const adminClientId = params.get("client_id");
  const scope = adminClientId || "self";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSeen, setLastSeen] = useState(() => readLastSeen(scope));
  const markedRef = useRef(false);

  useEffect(() => {
    // Reset markedRef and re-read lastSeen when scope changes
    markedRef.current = false;
    setLastSeen(readLastSeen(scope));
  }, [scope]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = adminClientId ? `?client_id=${encodeURIComponent(adminClientId)}` : "";
        const { data } = await api.get(`/client-portal/activity${qs}`);
        if (!cancelled) setEvents(data?.events || []);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminClientId]);

  // Count events newer than lastSeen (treat missing ts as old).
  const unreadCount = useMemo(() => {
    if (!events.length) return 0;
    return events.filter((e) => {
      if (!e.ts) return false;
      const t = new Date(e.ts).getTime();
      return Number.isFinite(t) && t > lastSeen;
    }).length;
  }, [events, lastSeen]);

  const markAllRead = () => {
    const now = Date.now();
    writeLastSeen(scope, now);
    setLastSeen(now);
    markedRef.current = true;
  };

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="activity-feed-card">
      <CardContent className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2"
          style={{ background: `${accent}11` }}>
          <Activity className="h-3.5 w-3.5" style={{ color: accent }} />
          <div className="font-display font-bold text-sm tracking-tight">Recent activity</div>
          {unreadCount > 0 && (
            <span className="relative inline-flex items-center" data-testid="activity-unread-dot">
              <span className="absolute -inset-1 rounded-full bg-red-500 opacity-60 animate-ping" />
              <span className="relative inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold px-1.5 h-4 rounded-full">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            </span>
          )}
          <div className="flex-1" />
          {unreadCount > 0 && (
            <button type="button" onClick={markAllRead}
              className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
              title="Mark all activity items as read"
              data-testid="activity-mark-read">
              <Check className="h-3 w-3" /> Mark read
            </button>
          )}
        </div>
        {loading ? (
          <div className="p-6 text-sm text-zinc-500 text-center">
            <Loader2 className="h-4 w-4 inline animate-spin mr-1.5" /> Loading…
          </div>
        ) : events.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500 text-center" data-testid="activity-empty">
            No activity yet.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {events.map((e, i) => {
              const Icon = ICON_BY_KIND[e.icon] || Activity;
              const eventTs = e.ts ? new Date(e.ts).getTime() : 0;
              const isUnread = eventTs > lastSeen;
              const body = (
                <div className={`px-4 py-2.5 flex items-start gap-3 hover:bg-zinc-50 ${isUnread ? "bg-amber-50/40" : ""}`}>
                  <div className="h-7 w-7 rounded-sm flex items-center justify-center text-white flex-shrink-0 relative"
                    style={{ background: accent }}>
                    <Icon className="h-3.5 w-3.5" />
                    {isUnread && (
                      <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"
                        data-testid={`activity-event-unread-${i}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium text-zinc-900 truncate">{e.title}</div>
                    {e.subtitle && (
                      <div className="text-[11px] text-zinc-500 truncate">{e.subtitle}</div>
                    )}
                  </div>
                  {e.ts && (
                    <div className="text-[10px] font-mono text-zinc-400 flex-shrink-0">
                      {new Date(e.ts).toLocaleDateString()}
                    </div>
                  )}
                </div>
              );
              return (
                <li key={`act-${i}`} data-testid={`activity-event-${e.kind}-${i}`}>
                  {e.link ? (
                    <Link to={e.link + (adminClientId ? `${e.link.includes("?") ? "&" : "?"}client_id=${encodeURIComponent(adminClientId)}` : "")}>
                      {body}
                    </Link>
                  ) : body}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
